var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import { Box, Button, Card, CardContent, Divider, FormControl, InputLabel, MenuItem, Select, Typography, } from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useListMergeableTeams, useProcessOwnershipInvitation, useRetrieveOwnershipInvitation, } from "../api/InvitationApiProvider";
import { ErrorView } from "../nav/Error";
import { Loading } from "../nav/Loading";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { useNumericParams } from "../utils/useNumericParams";
export var OwnershipInvitationDetail = function () {
    var id = useNumericParams().id;
    var _a = useRetrieveOwnershipInvitation({ id: id }), invitation = _a.data, isLoading = _a.isLoading, error = _a.error;
    var navigate = useNavigate();
    var _b = __read(React.useState(""), 2), errorMessage = _b[0], setErrorMessage = _b[1];
    var _c = __read(useState(""), 2), mergeIntoTeamId = _c[0], setMergeIntoTeamId = _c[1];
    var isPending = (invitation === null || invitation === void 0 ? void 0 : invitation.status) === "PENDING";
    var _d = useListMergeableTeams({
        invitationId: id,
        enabled: isPending,
    }), mergeableTeams = _d.data, isMergeLoading = _d.isLoading;
    var acceptMutation = useProcessOwnershipInvitation({
        invitation: invitation,
        onMutate: function () { },
        onSuccess: function () {
            var targetTeam = typeof mergeIntoTeamId === "number"
                ? mergeIntoTeamId
                : invitation.team;
            navigate("/teams/".concat(targetTeam));
        },
        onError: function () { return setErrorMessage("Failed to accept ownership invitation."); },
    });
    var declineMutation = useProcessOwnershipInvitation({
        invitation: invitation,
        onMutate: function () { },
        onSuccess: function () { return navigate("/ownership-invitations"); },
        onError: function () { return setErrorMessage("Failed to decline ownership invitation."); },
    });
    if (isLoading || isMergeLoading) {
        return _jsx(Loading, {});
    }
    if (error || !invitation) {
        return _jsx(ErrorView, { error: error });
    }
    var isProcessing = acceptMutation.isLoading || declineMutation.isLoading;
    return (_jsxs(React.Fragment, { children: [_jsx(Typography, { variant: "h4", gutterBottom: true, children: "Ownership Invitation" }), _jsx(Divider, { sx: { my: 2 } }), _jsx(Card, { variant: "outlined", sx: { maxWidth: 600 }, children: _jsxs(CardContent, { children: [_jsx(Typography, { variant: "h5", gutterBottom: true, children: invitation.teamName }), _jsx(Typography, { variant: "body1", color: "text.secondary", sx: { mb: 1 }, children: "You've been invited to take ownership of this team" }), _jsxs(Typography, { variant: "body1", color: "text.secondary", sx: { mb: 1 }, children: ["Invited by ", invitation.inviterFirstName, " ", invitation.inviterLastName] }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 2 }, children: formatDistanceToNow(new Date(invitation.createdAt), {
                                addSuffix: true,
                            }) }), invitation.message && (_jsxs(Typography, { variant: "body1", sx: {
                                mb: 2,
                                fontStyle: "italic",
                                p: 2,
                                bgcolor: "grey.50",
                                borderRadius: 1,
                            }, children: ["\u201C", invitation.message, "\u201D"] })), isPending && mergeableTeams && mergeableTeams.length > 0 && (_jsxs(FormControl, { fullWidth: true, sx: { mb: 2 }, children: [_jsx(InputLabel, { children: "Merge Into Existing Team (Optional)" }), _jsxs(Select, { value: mergeIntoTeamId, onChange: function (e) {
                                        return setMergeIntoTeamId(e.target.value);
                                    }, label: "Merge Into Existing Team (Optional)", children: [_jsx(MenuItem, { value: "", children: _jsx("em", { children: "None \u2014 keep as separate team" }) }), mergeableTeams.map(function (team) { return (_jsx(MenuItem, { value: team.id, children: team.name }, team.id)); })] }), _jsx(Typography, { variant: "caption", color: "text.secondary", sx: { mt: 0.5 }, children: "Optionally merge this team's data into one of your existing teams. The original team will be deleted." })] })), isPending ? (_jsxs(Box, { sx: { display: "flex", gap: 2, mt: 2 }, children: [_jsx(Button, { variant: "contained", color: "success", startIcon: _jsx(CheckCircleIcon, {}), disabled: isProcessing, onClick: function () {
                                        return acceptMutation.mutate(__assign({ id: invitation.id, status: "ACCEPTED" }, (mergeIntoTeamId
                                            ? { mergeIntoTeamId: mergeIntoTeamId }
                                            : {})));
                                    }, children: acceptMutation.isLoading ? "Accepting..." : "Accept" }), _jsx(Button, { variant: "outlined", color: "error", startIcon: _jsx(DoNotDisturbIcon, {}), disabled: isProcessing, onClick: function () {
                                        return declineMutation.mutate({
                                            id: invitation.id,
                                            status: "DECLINED",
                                        });
                                    }, children: declineMutation.isLoading ? "Declining..." : "Decline" })] })) : (_jsx(Typography, { variant: "body1", color: invitation.status === "ACCEPTED" ? "success.main" : "error.main", sx: { mt: 2, fontWeight: "bold" }, children: invitation.status === "ACCEPTED"
                                ? "You accepted this ownership invitation"
                                : "You declined this ownership invitation" }))] }) }), _jsx(ErrorSnackbar, { open: !!errorMessage, onClose: function () { return setErrorMessage(""); }, message: errorMessage })] }));
};
